import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
  MotifButton,
  MotifTruncate,
  MotifProgressLoader,
  MotifSelect,
  MotifOption,
  MotifAvatar,
  MotifIcon,
} from "@ey-xd/motif-react";
import { RootState, AppDispatch } from "../../store";
import { getFormattedDate, getTotalOrderAmount } from "../MyWorkspace/helper";
import {
  fetchSharedUserListData,
  resetSharedUserList,
  shareEstimate,
  updateEstimateAccess,
} from "../../store/slices/ShareEstimateSlice";

import productListEmptyImg from "../../assets/images/empty_product_list_default.svg";
import ErrorOrderPage from "../ErrorOrderPage/ErrorOrderPage";
import NotificationGrid from "../Notification/NotificationGrid";
import EmailSearch from "../EmailSearch/EmailSearch";
import "./ShareEstimateModal.scss";
import {
  IconoirArrowLeft,
  IconoirSettings,
  IconoirMoreHoriz,
} from "@ey-xd/motif-icon";
import emptySharedListImg from "../../assets/images/empty_shared_users_list.svg";
import { resetUserSearchData } from "../../store/slices/UsersSlice";

interface Order {
  OrderParentId: string;
  Description: string;
  CreatedDtm: string;
  StatusCd: string;
  OpportunityId?: string;
}

interface UserList {
  Name: string;
  Email: string;
  UserId?: number;
  EntityUserRoleId?: number;
  RoleCd?: string;
  EntityType?: string;
  EntityId?: number;
}

interface UserAccess {
  UserId: number;
  UserAction: string;
}

type ShareEstimateProps = {
  show: boolean;
  setShow: (show: boolean) => void;
  sharedOrder: Order;
  handleShareEstimateSuccess: () => void;
};

export default function ShareEstimateModal({
  show,
  setShow,
  sharedOrder,
  handleShareEstimateSuccess,
}: ShareEstimateProps) {
  const [page, setPage] = useState<number>(1);

  const dispatch = useDispatch<AppDispatch>();
  const [sharedUserList, setSharedUserList] = useState<UserList[]>([]);
  const [newUserList, setNewUserList] = useState<UserList[]>([]);
  const [sharedEmailValidationError, setSharedEmailValidationError] =
    useState<boolean>(false);
  const [isShareEstimateLoading, setIsShareEstimateLoading] =
    useState<boolean>(false);
  const [shareEstimateError, setShareEstimateError] = useState<string>("");
  const isUserListLoading = useSelector(
    (state: RootState) => state.ShareEstimateSlice.isLoading,
  );
  const sharedUsersData = useSelector(
    (state: RootState) => state.ShareEstimateSlice.data,
  );
  const sharedUsersError = useSelector(
    (state: RootState) => state.ShareEstimateSlice.error,
  );

  const [isAccessUpdateLoading, setIsAccessUpdateLoading] =
    useState<boolean>(false);
  const [updateAccessError, setUpdateAccessError] = useState<string>("");
  const [updateAccessSuccess, setUpdateAccessSuccess] = useState<string>("");
  const [updateAccessList, setUpdateAccessList] = useState<UserAccess[]>([]);

  useEffect(() => {
    if (shareEstimateError) {
      const timer = setTimeout(() => {
        setShareEstimateError("");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [shareEstimateError]);

  useEffect(() => {
    resetSharedUserList();
    const payload = {
      Action: "estimateSharedUser",
      OrderId: Number(sharedOrder.OrderParentId),
    };
    dispatch(fetchSharedUserListData(payload));
  }, [sharedOrder.OrderParentId]);

  useEffect(() => {
    if (sharedUsersData && sharedUsersData?.result) {
      if (sharedUsersData.result.length > 0) {
        setSharedUserList(
          sharedUsersData.result.map((user) => ({
            Name: user.Name,
            Email: user.Email,
          })),
        );
      } else {
        setSharedUserList([]);
      }
    }
  }, [sharedUsersData]);

  useEffect(() => {
    if (updateAccessSuccess) {
      const timer1 = setTimeout(() => {
        setUpdateAccessSuccess("");
      }, 3000);

      return () => clearTimeout(timer1);
    }

    if (updateAccessError) {
      const timer2 = setTimeout(() => {
        setUpdateAccessError("");
      }, 3000);

      return () => clearTimeout(timer2);
    }
  }, [updateAccessSuccess, updateAccessError]);

  const closeShareModal = () => {
    dispatch(resetUserSearchData());
    setSharedUserList([]);
    setNewUserList([]);
    setSharedEmailValidationError(false);
    setShareEstimateError("");
    setShow(false);
    document.body.style.overflow = "";
  };

  const handleShareEstimate = async () => {
    try {
      setIsShareEstimateLoading(true);
      setShareEstimateError("");
      const payload = {
        Action: "shareEstimate",
        OrderId: Number(sharedOrder.OrderParentId),
        SharedUsers: [...newUserList, ...sharedUserList],
      };
      const response = await dispatch(shareEstimate(payload));
      if (response.payload.Status === "success") {
        setIsShareEstimateLoading(false);
        closeShareModal();
        handleShareEstimateSuccess();
      } else {
        setIsShareEstimateLoading(false);
        setShareEstimateError(
          "An error occurred while sharing the estimate. Please try again.",
        );
      }
    } catch (error) {
      setIsShareEstimateLoading(false);
      console.error("Failed to share estimate:", error);
      setShareEstimateError(
        "An error occurred while sharing the estimate. Please try again.",
      );
    }
  };

  const handleManageSharedUsers = () => {
    setUpdateAccessList([]);
    setPage(2);
  };

  const getUserActionByRoleCd = (roleCd: string) => {
    return roleCd === "order_viewer" ? "view" : "delete";
  };

  const getSharedUsers = () => {
    if (sharedUsersData && sharedUsersData?.result.length > 0) {
      return (
        <MotifButton
          type="button"
          size="medium"
          variant="secondary"
          onClick={handleManageSharedUsers}
        >
          <MotifIcon iconFunction={IconoirSettings} />
          Manage Access
          <div className="shared-user-avatars-container">
            <span className="share-estimate-avatars">
              <MotifAvatar
                key={sharedUsersData.owner[0].OwnerEmail}
                description={
                  sharedUsersData.owner[0].OwnerName +
                  " " +
                  sharedUsersData.owner[0].OwnerEmail
                }
                userName={sharedUsersData.owner[0].OwnerName}
                className=""
              />
              {sharedUsersData.result.map((user, index) => {
                if (index < 3) {
                  return (
                    <MotifAvatar
                      key={user.Email}
                      description={user.Name + " " + user.Email}
                      userName={user.Name}
                      className=""
                    />
                  );
                }
              })}
              {sharedUsersData.result.length > 3 && (
                <MotifAvatar
                  key={sharedUsersData.result.length + 1}
                  variant={"icon"}
                  src={IconoirMoreHoriz}
                  className=""
                />
              )}
            </span>
          </div>
        </MotifButton>
      );
    } else {
      return (
        <div className="empty-shared-users-container">
          <span className="empty-shared-users-img-wrapper">
            <img
              src={emptySharedListImg}
              alt="Empty Shared Users List Locked"
              height="20"
              width="20"
            />
          </span>
          <span>This estimate hasn't been shared yet</span>
        </div>
      );
    }
  };

  const handleCancelAccessChanges = () => {
    setPage(1);
    setUpdateAccessList([]);
  };

  const handleSaveAccessChanges = async () => {
    try {
      setIsAccessUpdateLoading(true);
      setUpdateAccessError("");
      setUpdateAccessSuccess("");
      const payload = {
        Action: "shareEstimateUpdate",
        OrderId: Number(sharedOrder.OrderParentId),
        SharedUsers: updateAccessList,
      };
      const response = await dispatch(updateEstimateAccess(payload));
      if (response.payload.Status === "success") {
        setIsAccessUpdateLoading(false);
        setUpdateAccessList([]);
        resetSharedUserList();
        const payload = {
          Action: "estimateSharedUser",
          OrderId: Number(sharedOrder.OrderParentId),
        };
        dispatch(fetchSharedUserListData(payload));
        setUpdateAccessSuccess("Changes saved successfully.");
      } else {
        setIsAccessUpdateLoading(false);
        setUpdateAccessError(
          "An error occurred while updating the estimate access. Please try again.",
        );
      }
    } catch (error) {
      setIsAccessUpdateLoading(false);
      console.error("Failed to update estimate access:", error);
      setUpdateAccessError(
        "An error occurred while updating the estimate access. Please try again.",
      );
    }
  };

  return (
    <MotifModal
      show={show}
      size="lg"
      focusTrapOptions={{
        tabbableOptions: {
          displayCheck: "none",
        },
      }}
      className="shareEstimateModalWrapper"
      onClose={closeShareModal}
    >
      {page === 1 ? (
        <>
          <MotifModalHeader>
            <span className="header-name">Share Estimate</span>
          </MotifModalHeader>
          <MotifModalBody>
            {isUserListLoading ? (
              <MotifProgressLoader
                aria-busy="true"
                aria-label="This page is loading"
                aria-live="polite"
                arialabel="This page is loading"
                show
                variant="default"
              />
            ) : (
              <>
                {sharedUsersError ? (
                  <div className="error-message-section">
                    <ErrorOrderPage
                      image={productListEmptyImg}
                      header="An error has occurred while attempting to retrieve data. Please choose the option to refresh the page."
                      handleRefresh={() => {
                        const payload = {
                          Action: "estimateSharedUser",
                          OrderId: Number(sharedOrder.OrderParentId),
                        };
                        dispatch(fetchSharedUserListData(payload));
                      }}
                    />
                  </div>
                ) : (
                  <>
                    {shareEstimateError && (
                      <div className="error-message-div">
                        <NotificationGrid
                          show={true}
                          type="error"
                          message={shareEstimateError}
                        />
                      </div>
                    )}
                    <table className="motif-table-standard">
                      <thead>
                        <tr>
                          <th style={{ width: "250px" }}>Estimate Name/ ID </th>
                          <th style={{ width: "165px" }}>Date Created </th>
                          <th style={{ width: "155px" }}>Opportunity Code </th>
                          <th style={{ width: "200px" }}>Est. Cost </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <div>
                              <MotifTruncate maxLines={1} maxWidth={200}>
                                {sharedOrder.Description}
                              </MotifTruncate>
                              <div className="order-id">
                                {sharedOrder.OrderParentId}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="order-date">
                              {getFormattedDate(sharedOrder.CreatedDtm)}
                            </div>
                          </td>
                          <td>
                            <div className="order-date">
                              {sharedOrder.OpportunityId || "----"}
                            </div>
                          </td>
                          <td>
                            <div className="order-total">
                              {getTotalOrderAmount(sharedOrder)}
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <EmailSearch
                      newUserList={newUserList}
                      setNewUserList={setNewUserList}
                      setSharedEmailValidationError={
                        setSharedEmailValidationError
                      }
                      sharedUserList={sharedUserList}
                    />
                  </>
                )}
              </>
            )}
          </MotifModalBody>
          <MotifModalFooter>
            <div className="footer-buttons">
              <MotifButton
                type="button"
                size="medium"
                variant="secondary"
                onClick={closeShareModal}
              >
                Cancel
              </MotifButton>
              <MotifButton
                variant="primary"
                size="medium"
                disabled={
                  isUserListLoading ||
                  sharedUsersError !== null ||
                  sharedEmailValidationError ||
                  !newUserList.length ||
                  isShareEstimateLoading
                }
                type="button"
                onClick={handleShareEstimate}
                className="ml-16"
              >
                Share Estimate
              </MotifButton>
            </div>
            {!isUserListLoading && <div>{getSharedUsers()}</div>}
          </MotifModalFooter>
        </>
      ) : (
        <>
          <MotifModalHeader>
            <span className="back-icon" onClick={handleCancelAccessChanges}>
              <MotifIcon iconFunction={IconoirArrowLeft} size="24" />
            </span>
            <span className="header-name">Manage Access</span>
          </MotifModalHeader>
          <MotifModalBody>
            {isAccessUpdateLoading ? (
              <MotifProgressLoader
                aria-busy="true"
                aria-label="This page is loading"
                aria-live="polite"
                arialabel="This page is loading"
                show
                variant="default"
              />
            ) : (
              <>
                {updateAccessSuccess && (
                  <div className="error-message-div">
                    <NotificationGrid
                      show={true}
                      type="success"
                      message={updateAccessSuccess}
                    />
                  </div>
                )}
                {updateAccessError && (
                  <div className="error-message-div">
                    <NotificationGrid
                      show={true}
                      type="error"
                      message={updateAccessError}
                    />
                  </div>
                )}
                <div className="estimate-name">
                  <MotifTruncate maxLines={1} maxWidth={250}>
                    {sharedOrder.Description}
                  </MotifTruncate>
                </div>
                <div className="estimate-id">{sharedOrder.OrderParentId}</div>

                <table className="motif-table-standard manage-access-table">
                  <thead>
                    <tr>
                      <th style={{ width: "280px" }}>Name</th>
                      <th style={{ width: "400px" }}>Email</th>
                      <th style={{ width: "173px" }}>Access</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{sharedUsersData?.owner[0]?.OwnerName}</td>
                      <td>{sharedUsersData?.owner[0]?.OwnerEmail}</td>
                      <td>Owner</td>
                    </tr>
                    {sharedUsersData &&
                      sharedUsersData?.result?.map((user: any) => {
                        return (
                          <tr key={user.Email}>
                            <td>{user.Name}</td>
                            <td>{user.Email}</td>
                            <td>
                              <MotifSelect
                                ariaLabelledBy="select-access-label"
                                onChange={(val: any) => {
                                  const selectedAction =
                                    val === "order_viewer" ? "view" : "delete";
                                  const originalAction = getUserActionByRoleCd(
                                    user.RoleCd,
                                  );
                                  const existingAccessIndex =
                                    updateAccessList.findIndex(
                                      (access) => access.UserId === user.UserId,
                                    );
                                  const updatedAccessList = [
                                    ...updateAccessList,
                                  ];

                                  // Keep only real changes from original role.
                                  if (selectedAction === originalAction) {
                                    if (existingAccessIndex !== -1) {
                                      updatedAccessList.splice(
                                        existingAccessIndex,
                                        1,
                                      );
                                    }
                                  } else if (existingAccessIndex !== -1) {
                                    updatedAccessList[
                                      existingAccessIndex
                                    ].UserAction = selectedAction;
                                  } else {
                                    updatedAccessList.push({
                                      UserId: user.UserId,
                                      UserAction: selectedAction,
                                    });
                                  }
                                  setUpdateAccessList(updatedAccessList);
                                }}
                                placeholder=""
                                value={user.RoleCd}
                              >
                                <MotifOption
                                  key="order_viewer"
                                  value="order_viewer"
                                >
                                  View
                                </MotifOption>
                                <MotifOption
                                  key="order_delete"
                                  value="order_delete"
                                >
                                  Remove
                                </MotifOption>
                              </MotifSelect>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </>
            )}
          </MotifModalBody>
          <MotifModalFooter>
            <div className="footer-buttons">
              <MotifButton
                type="button"
                size="medium"
                variant="secondary"
                onClick={handleCancelAccessChanges}
              >
                Cancel
              </MotifButton>
              <MotifButton
                variant="primary"
                size="medium"
                disabled={
                  isAccessUpdateLoading || updateAccessList.length === 0
                }
                type="button"
                onClick={handleSaveAccessChanges}
                className="ml-16"
              >
                Save Changes
              </MotifButton>
            </div>
          </MotifModalFooter>
        </>
      )}
    </MotifModal>
  );
}
